import { useEffect, useState } from "react";
import "../inward-styles.css";
import Icon from "@mdi/react";
import { mdiMagnify, mdiCloseCircleOutline, mdiEmailOutline } from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import "../inward-styles.css";
import LoadingWidget from "../../../../context_providers/modal/loader";
import InventoryStockList from "./InventoryStockList";
import StockByWarehouse from "./StockByWarehouse";
import PaginationWarehouse from "../inward/PaginationWarehouse";
import { fetchGet } from "../../content_panel/services/serviceClient";
import WarehouseSearchFieldByNameID from "../common-components/WarehouseSearchFieldByNameID";

const DEFAULT_PAGINATION = {
  page: 1,
  totalPages: 1,
  total: 0,
  limit: 100,
  hasMore: false,
};

const DEFAULT_KPIS = {
  total_stock_qty: 0,
  total_pending_outwards: 0,
  total_in_transit: 0,
  total_faulty_stock: 0,
  total_items: 0,
  total_warehouses: 0,
  total_stock_value: 0,
};

const WAREHOUSE_SUB_LEVEL_OPTIONS = [
  "Manpower",
  "Job Work",
  "RWA",
  "Infra",
  "Kazam Warehouse",
  "Lab",
];

const InventoryDashboard = () => {
  const [filters, setFilters] = useState({ search: "", warehouseName: "" });
  const [warehouseId, setWarehouseId] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [subLevel, setSubLevel] = useState("");
  const [asOfDate, setAsOfDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const todayStr = new Date().toLocaleDateString("en-CA", {
    timeZone: "Asia/Kolkata",
  });
  const isMovementView = !!fromDate;
  const [page, setPage] = useState(1);

  const [isLoading, setIsLoading] = useState(false);

  const [kpis, setKpis] = useState(DEFAULT_KPIS);
  const [stockSummary, setStockSummary] = useState<any[]>([]);
  const [stockByWarehouse, setStockByWarehouse] = useState<any[]>([]);
  const [stockSummaryPagination, setStockSummaryPagination] =
    useState(DEFAULT_PAGINATION);

  const fetchDashboard = async (
    search = "",
    currentPage = 1,
    whId = "",
    subCat = "",
    asOf = "",
    from = "",
  ) => {
    try {
      setIsLoading(true);

      const query = new URLSearchParams();
      if (search) query.append("search", search);
      if (whId) query.append("warehouse_id", whId);
      if (subCat) query.append("sub_category", subCat);
      if (asOf) query.append("as_of_date", asOf);
      if (from) query.append("from_date", from);
      if (subLevel) query.append("sub_level", subLevel);
      query.append("page", String(currentPage));

      const res = await fetchGet(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/create-grn-by-spoc/warehouse-inventory-dashboard?${query.toString()}`,
      );

      if (res.success) {
        setKpis(res.data.kpis || DEFAULT_KPIS);
        setStockSummary(res.data.stockSummary || []);
        setStockByWarehouse(res.data.stockByWarehouse || []);
        setStockSummaryPagination(
          res.data.stockSummaryPagination || DEFAULT_PAGINATION,
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(filters.search.trim());
    }, 400);

    return () => clearTimeout(handler);
  }, [filters.search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, warehouseId, subCategory, asOfDate, fromDate, subLevel]);

  useEffect(() => {
    fetchDashboard(
      debouncedSearch,
      page,
      warehouseId,
      subCategory,
      asOfDate,
      fromDate,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    page,
    warehouseId,
    subCategory,
    asOfDate,
    fromDate,
    subLevel,
  ]);

  const [exporting, setExporting] = useState(false);
  const { showToast } = useToast();

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      if (debouncedSearch) qs.append("search", debouncedSearch);
      if (warehouseId) qs.append("warehouse_id", warehouseId);
      if (subCategory) qs.append("sub_category", subCategory);
      if (asOfDate) qs.append("as_of_date", asOfDate);
      if (subLevel) qs.append("sub_level", subLevel);
      const res = await fetchGet(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/create-grn-by-spoc/export-inventory-by-email?${qs.toString()}`,
      );
      if (res?.success) {
        showToast({
          type: ToastType.success,
          text: res.message || "Export will be emailed shortly.",
        });
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to schedule export",
        });
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e?.message || "Failed to schedule export",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 min-h-screen">
      <div className="p-4 md:p-6 animate-fade-in">
        <div className="flex flex-wrap justify-between items-end gap-3">
          <h1 className="text-xl md:text-3xl font-bold border-l-4 border-myPrimaryColor pl-2">
            Inventory Overview
          </h1>
          <button
            id="warehouse-inventory-export-email-btn"
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Icon path={mdiEmailOutline} size={0.8} />
            {exporting ? "Scheduling…" : "Export by Email"}
          </button>
        </div>

        <div className="flex gap-4 overflow-x-auto md:grid md:grid-cols-7 py-2 mt-4">
          <div className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-success bg-white shadow-sm">
            <div className="p-3 md:p-4">
              <p className="text-xs text-muted-foreground">Total Stock</p>
              <p className="text-lg md:text-2xl font-bold">
                {(kpis.total_stock_qty ?? 0).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-warning bg-white shadow-sm">
            <div className="p-3 md:p-4">
              <p className="text-xs text-muted-foreground">Pending Outwards</p>
              <p className="text-lg md:text-2xl font-bold">
                {(kpis.total_pending_outwards ?? 0).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-primary bg-white shadow-sm">
            <div className="p-3 md:p-4">
              <p className="text-xs text-muted-foreground">In Transit</p>
              <p className="text-lg md:text-2xl font-bold">
                {(kpis.total_in_transit ?? 0).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-red-500 bg-white shadow-sm">
            <div className="p-4">
              <p className="text-sm text-muted-foreground">Faulty Items</p>
              <p className="text-2xl font-bold text-red-600">
                {(kpis.total_faulty_stock ?? 0).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-info bg-white shadow-sm">
            <div className="p-3 md:p-4">
              <p className="text-xs text-muted-foreground">Total Items</p>
              <p className="text-lg md:text-2xl font-bold">
                {(kpis.total_items ?? 0).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-destructive bg-white shadow-sm">
            <div className="p-3 md:p-4">
              <p className="text-xs text-muted-foreground">Total Warehouses</p>
              <p className="text-lg md:text-2xl font-bold">
                {(kpis.total_warehouses ?? 0).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-success bg-white shadow-sm">
            <div className="p-3 md:p-4">
              <p className="text-xs text-muted-foreground">Total Stock Value</p>
              <p className="text-lg md:text-2xl font-bold">
                ₹
                {(kpis.total_stock_value ?? 0).toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6 mt-4">
          <div className="rounded-lg border bg-white p-4 relative z-20 overflow-visible">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
                <Icon
                  path={mdiMagnify}
                  className="absolute left-3 top-1/3 -translate-y-1/2 h-5 w-5 text-muted-foreground"
                />
                <input
                  id="warehouse-inventory-dashboard-search-input"
                  className="h-10 w-full rounded-md border px-3 pl-9 pr-3 text-xs md:text-sm"
                  placeholder="Search by item code or name..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, search: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setDebouncedSearch(filters.search.trim());
                      setPage(1);
                    }
                  }}
                />
              </div>
              {filters.search && (
                <button
                  id="warehouse-inventory-dashboard-search-clear-btn"
                  onClick={() => {
                    setFilters((p) => ({ ...p, search: "" }));
                    setDebouncedSearch("");
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Clear Search"
                >
                  <Icon path={mdiCloseCircleOutline} size={0.7} />
                </button>
              )}
              <div className="w-full sm:min-w-[250px]">
                <WarehouseSearchFieldByNameID
                  placeholder="Filter by Warehouse"
                  value={filters.warehouseName}
                  onChange={(val) =>
                    setFilters((p) => ({ ...p, warehouseName: val }))
                  }
                  onSelect={(wh) => setWarehouseId(wh.id || "")}
                  onClear={() => {
                    setFilters((p) => ({ ...p, warehouseName: "" }));
                    setWarehouseId("");
                  }}
                />
              </div>
              <select
                id="warehouse-inventory-item-type-select"
                className="h-10 rounded-md border bg-white px-3 text-xs md:text-sm w-full sm:w-auto sm:min-w-[180px]"
                value={subCategory}
                onChange={(e) => setSubCategory(e.target.value)}
              >
                <option value="">All Item Types</option>
                <option value="Finished Goods">Finished Goods</option>
                <option value="Raw Material">Raw Material</option>
              </select>
              <select
                id="warehouse-inventory-sub-level-select"
                className="h-10 rounded-md border bg-white px-3 text-xs md:text-sm w-full sm:w-auto sm:min-w-[180px]"
                value={subLevel}
                onChange={(e) => setSubLevel(e.target.value)}
              >
                <option value="">All Sub-Levels</option>
                {WAREHOUSE_SUB_LEVEL_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="relative w-full sm:w-auto">
                  <input
                    id="warehouse-inventory-from-date-input"
                    type="date"
                    className="h-10 rounded-md border bg-white px-3 text-xs md:text-sm w-full sm:w-auto sm:min-w-[150px]"
                    value={fromDate}
                    max={asOfDate || todayStr}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFromDate(v);
                      // Movement view defaults the To Date to today.
                      if (v && !asOfDate) setAsOfDate(todayStr);
                    }}
                    title="From Date"
                  />
                  {fromDate && (
                    <button
                      id="warehouse-inventory-from-date-clear-btn"
                      onClick={() => setFromDate("")}
                      className="absolute -right-5 top-1/3 -translate-y-1/2 text-gray-400 hover:text-red-500"
                      title="Clear From date"
                    >
                      <Icon path={mdiCloseCircleOutline} size={0.7} />
                    </button>
                  )}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                  to
                </span>
                <div className="relative w-full sm:w-auto">
                  <input
                    id="warehouse-inventory-to-date-input"
                    type="date"
                    className="h-10 rounded-md border bg-white px-3 text-xs md:text-sm w-full sm:w-auto sm:min-w-[150px]"
                    value={asOfDate}
                    min={fromDate || undefined}
                    onChange={(e) => setAsOfDate(e.target.value)}
                    title={isMovementView ? "To Date" : "As of Date"}
                  />
                  {asOfDate && (
                    <button
                      id="warehouse-inventory-to-date-clear-btn"
                      onClick={() => setAsOfDate("")}
                      className="absolute -right-5 top-1/3 -translate-y-1/2 text-gray-400 hover:text-red-500"
                      title="Clear date"
                    >
                      <Icon path={mdiCloseCircleOutline} size={0.7} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
            <span>{stockSummaryPagination.total} records</span>
            <span>
              Page {stockSummaryPagination.page} of{" "}
              {stockSummaryPagination.totalPages}
            </span>
          </div>

          <InventoryStockList
            data={stockSummary}
            isMovementView={isMovementView}
          />

          <PaginationWarehouse
            page={page}
            totalPages={stockSummaryPagination.totalPages || 1}
            onPageChange={(newPage) => {
              if (
                newPage >= 1 &&
                newPage <= (stockSummaryPagination.totalPages || 1)
              ) {
                setPage(newPage);
              }
            }}
          />

          <StockByWarehouse data={stockByWarehouse} />
        </div>
      </div>

      {isLoading && <LoadingWidget />}
    </div>
  );
};

export default InventoryDashboard;
